import Text from '@atoms/Text/Text';
import { useMemo, useState } from 'react';
import { handleVoucherData } from '@helpers/HandlePersonalElementsData';
import { useTranslations } from 'next-intl';
import { trackPersonalElementsVouchersOnClick } from '@helpers/analyticsHelpers/trackPersonalElements';
import Button from '@atoms/Button/Button';
import type { AxfoodExternalVoucher } from '@occ/api-client';
import { StyledPersonalElementsCard, StyledPrice, StyledText } from './PersonalElementsVoucher.styles';
import { StyledGenericPersonalCardTextWrapper } from '@molecules/PersonalElementsCards/GenericPersonalCard.styles';
import VoucherModal from '@components/molecules/VoucherModal/VoucherModal';

export enum discountTypes {
  ABSOLUTE = 'Absolut värde',
}

interface Props {
  onlineVouchers: AxfoodExternalVoucher[] | undefined;
  offlineVouchers: AxfoodExternalVoucher[] | undefined;
}

const PersonalElementsVoucher = ({ onlineVouchers, offlineVouchers }: Props) => {
  const [showVoucherModal, setShowVoucherModal] = useState<boolean>(false);
  const [selectedVoucher, setSelectedVoucher] = useState<AxfoodExternalVoucher>({} as AxfoodExternalVoucher);
  const t = useTranslations('personalElements');

  const allAvailableVouchers = useMemo(() => {
    const addIndex = (vouchers: AxfoodExternalVoucher[] = []) =>
      vouchers.map((voucher, index) => ({ ...voucher, index }));

    // Combine all vouchers and remove duplicates by voucherId
    const allVouchers = [...addIndex(onlineVouchers), ...addIndex(offlineVouchers)];
    const uniqueVouchers = Array.from(new Map(allVouchers.map((v) => [v.voucherId, v])).values());

    // Sort by expiration date (earliest first)
    uniqueVouchers.sort((a, b) => {
      const getTime = (date?: string) => (date ? new Date(date).getTime() : Number.MAX_SAFE_INTEGER);
      return getTime(a.expirationDate) - getTime(b.expirationDate);
    });

    return uniqueVouchers;
  }, [onlineVouchers, offlineVouchers]);

  const voucherClickHandler = (voucher: AxfoodExternalVoucher) => {
    trackPersonalElementsVouchersOnClick();
    setSelectedVoucher(voucher);
    setShowVoucherModal(true);
  };

  return (
    <>
      {allAvailableVouchers.map((voucher) => {
        const cardData = handleVoucherData(voucher, t);
        return (
          <StyledPersonalElementsCard key={`${voucher.index}-${voucher.voucherId}`}>
            <StyledGenericPersonalCardTextWrapper>
              <Text type="label">
                {cardData.title.split(':')[0]}
                <StyledPrice>{cardData.title.split(':')[1]}</StyledPrice>
              </Text>

              <StyledText type="body" size="medium">
                {cardData.text}
              </StyledText>
            </StyledGenericPersonalCardTextWrapper>
            <Button
              type="button"
              variant="secondary-light-bg"
              size="small"
              onClick={() => voucherClickHandler(voucher)}
            >
              <Text type="subhead" size="small">
                {cardData.button}
              </Text>
            </Button>
          </StyledPersonalElementsCard>
        );
      })}

      {showVoucherModal && selectedVoucher && (
        <VoucherModal
          voucher={selectedVoucher}
          onClose={() => setShowVoucherModal(false)}
          onConfirm={() => setShowVoucherModal(false)}
        />
      )}
    </>
  );
};

export default PersonalElementsVoucher;
